import {
  BiologicalParentData,
  ClinicalData,
  FamilyHistoryData,
  HPOOption,
  OrderData,
  PatientData,
  ReasonForTestingData,
  RequisitionFormData,
  TestType,
} from '@/types/RequisitionFormTypes'
import validator from 'validator'

export class FormCompletionChecker {
  static isOrderComplete(data: OrderData): boolean {
    return Boolean(data.selectedOption && data.selectedOption.trim() !== '')
  }

  static isPatientComplete(data: PatientData): boolean {
    return Boolean(
      data.firstName &&
        data.firstName.trim() !== '' &&
        data.lastName &&
        data.lastName.trim() !== '' &&
        data.dateOfBirth &&
        data.dateOfBirth.trim() !== '' &&
        data.sex &&
        data.sex.trim() !== '' &&
        data.mrn &&
        data.mrn.trim() !== '' &&
        data.collectionType &&
        data.collectionType.trim() !== ''
    )
  }

  static isClinicalComplete(data: ClinicalData): boolean {
    const clinicalNote = Boolean(data.clinicalNote && data.clinicalNote.trim() !== '')
    const optOut = Boolean(data.optOutClinicianNotes)
    return clinicalNote || optOut
  }

  static isReasonComplete(data: ReasonForTestingData): boolean {
    // Helper function to check if clinical diagnosis has content
    const hasClinicalDiagnosis = () => {
      if (Array.isArray(data.clinicalDiagnosis)) {
        // Check if it's HPOOption[] or CodeableConcept[]
        if (data.clinicalDiagnosis.length > 0 && 'node' in data.clinicalDiagnosis[0]) {
          return (data.clinicalDiagnosis as HPOOption[]).length > 0
        }
        return data.clinicalDiagnosis.length > 0
      }
      return typeof data.clinicalDiagnosis === 'string' && data.clinicalDiagnosis.trim() !== ''
    }

    const clinicalDiagnosis = Boolean(hasClinicalDiagnosis())
    const icd10Diagnosis = Boolean(
      (Array.isArray(data.icd10Diagnosis) && data.icd10Diagnosis.length > 0) ||
        (typeof data.icd10Diagnosis === 'string' && data.icd10Diagnosis.trim() !== '')
    )
    const userInteracted = Boolean(data.userInteracted)

    return userInteracted && (clinicalDiagnosis || icd10Diagnosis)
  }

  static isFamilyComplete(data: FamilyHistoryData): boolean {
    return Boolean(data.familyHistory && data.familyHistory.trim() !== '')
  }

  static isBiologicalParentComplete(data?: BiologicalParentData): boolean {
    if (!data) {
      return false
    }
    return Boolean(
      data.parentFirstName &&
        data.parentFirstName.trim() !== '' &&
        data.parentLastName &&
        data.parentLastName.trim() !== '' &&
        data.parentDateOfBirth &&
        data.parentDateOfBirth.trim() !== '' &&
        data.parentMRN &&
        data.parentMRN.trim() !== '' &&
        data.parentPhone &&
        data.parentPhone.trim() !== '' &&
        data.parentEmail &&
        data.parentEmail.trim() !== '' &&
        data.phenotypicTrait &&
        data.collectionType &&
        data.collectionType.trim() !== '' &&
        validator.isEmail(data.parentEmail) &&
        validator.isMobilePhone(data.parentPhone)
    )
  }

  static isConsentComplete(): boolean {
    // Special case for consent (temporarily bypassed)
    return true
  }

  static isSectionComplete(section: keyof RequisitionFormData, formData: RequisitionFormData): boolean {
    switch (section) {
      case 'order':
        return this.isOrderComplete(formData.order)
      case 'patient':
        return this.isPatientComplete(formData.patient)
      case 'clinical':
        return this.isClinicalComplete(formData.clinical)
      case 'reason':
        return this.isReasonComplete(formData.reason)
      case 'family':
        return this.isFamilyComplete(formData.family)
      case 'mother':
      case 'father': {
        // Skip parent validation for rWGS Patient Only (F10-01)
        if (formData.order.selectedOption === TestType.rWGS_Patient_Only) {
          return true
        }
        // For rWGS Duo (F10-02), mother section is complete if either mother or father data is complete
        if (formData.order.selectedOption === TestType.rWGS_Duo) {
          return this.isBiologicalParentComplete(formData.mother) || this.isBiologicalParentComplete(formData.father)
        }
        const targetParent = section === 'mother' ? formData.mother : formData.father
        // For rWGS Trio (F10-03), mother section is complete if mother data is complete
        if (formData.order.selectedOption === TestType.rWGS_Trio) {
          return this.isBiologicalParentComplete(targetParent)
        }
        // For urWGS (F11-03), mother section is complete if mother data is complete
        if (formData.order.selectedOption === TestType.urWGS) {
          return this.isBiologicalParentComplete(targetParent)
        }
        return false
      }
      case 'consent':
        return this.isConsentComplete()
      default:
        return false
    }
  }

  // Helper method to check if at least one parent is complete for Duo tests
  static isAtLeastOneParentComplete(formData: RequisitionFormData): boolean {
    if (formData.order.selectedOption === TestType.rWGS_Duo) {
      return this.isBiologicalParentComplete(formData.mother) || this.isBiologicalParentComplete(formData.father)
    }
    return false
  }
}
